import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState } from "react";

import {
  FeatureAccessEnvironmentPanel,
  FeatureEnvironmentToggleCell,
} from "./FeatureAccessEnvironmentPanel";
import { FEATURE_ACCESS_UI_ENVIRONMENTS } from "./featureAccessEnvironments";
import type { FeatureAccessDefinition } from "./featureAccessRegistry";

import { useSetAdminV2FeatureReadinessMutation } from "@/store/api/baseApi";
import type { AdminV2FeatureAccessEnvironment } from "@/types/adminV2";
import { VNextSwitch } from "@/vnext/primitives";
import {
  extractPublicMutationError,
  type PublicMutationErrorDetails,
} from "@/vnext/routes/settings/extractPublicMutationError";

export interface FeatureConsoleFeedback {
  tone: "info" | "danger";
  details: PublicMutationErrorDetails;
}

/**
 * Request to confirm an all-organisations change. The owning table renders a
 * single dialog from these so the modal lives outside the table element.
 */
export interface FeatureConfirmRequest {
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
}

interface FeatureAccessRowProps {
  feature: FeatureAccessDefinition;
  canManage: boolean;
  /** Whether the feature is currently shown to normal admins. */
  ready: boolean;
  /** Dev staff (`featureflags.manage`) see the readiness toggle column. */
  isDevStaff: boolean;
  /** Column count, so the expanded detail cell can span the full row. */
  columnCount: number;
  onFeedback: (feedback: FeatureConsoleFeedback) => void;
  onRequestConfirm: (request: FeatureConfirmRequest) => void;
  /**
   * Called with the feature label when a rollout action is attempted while the
   * feature is not ready for admins. The table opens the "not ready" modal so
   * the rollout never reaches the entitlements store until it is marked ready.
   */
  onBlockedNotReady: (featureLabel: string) => void;
}

/**
 * One feature as a `<tbody>`: a main row (name, readiness toggle for dev
 * staff, one on-for-everyone toggle per site) plus an accordion detail row
 * with a Production/Staging tab bar over the per-organisation access list.
 *
 * @remarks
 * The two sites' entitlement stores are independent, so everything scoped to
 * an environment — the summary, the toggles, the staged organisations — lives
 * in {@link FeatureEnvironmentToggleCell} and
 * {@link FeatureAccessEnvironmentPanel}. Both panels stay mounted (the
 * inactive one is `hidden`) so switching tabs never drops staged-but-unsaved
 * organisations.
 *
 * When the feature is not ready for admins, the row stays visible but every
 * rollout control diverts to the table's "not ready" modal instead of writing,
 * so an in-development feature cannot be turned on before it is marked ready.
 */
export const FeatureAccessRow = ({
  feature,
  canManage,
  ready,
  isDevStaff,
  columnCount,
  onFeedback,
  onRequestConfirm,
  onBlockedNotReady,
}: FeatureAccessRowProps) => {
  const [expanded, setExpanded] = useState(false);
  const [activeEnvironment, setActiveEnvironment] =
    useState<AdminV2FeatureAccessEnvironment>(
      FEATURE_ACCESS_UI_ENVIRONMENTS[0].key,
    );
  const [setReadiness, readinessState] =
    useSetAdminV2FeatureReadinessMutation();

  const toggleReadiness = async (nextReady: boolean) => {
    try {
      await setReadiness({
        entitlementKey: feature.entitlementKey,
        ready: nextReady,
      }).unwrap();
      onFeedback({
        tone: "info",
        details: {
          message: nextReady
            ? `${feature.label} marked ready — it now appears for admins.`
            : `${feature.label} marked not ready — hidden from other admins.`,
        },
      });
    } catch (error) {
      onFeedback({
        tone: "danger",
        details: extractPublicMutationError(
          error,
          "Readiness could not be updated.",
        ),
      });
    }
  };

  return (
    <tbody
      className={`vnext-feature-row${
        expanded ? " vnext-feature-row--expanded" : ""
      }`}
    >
      <tr>
        <td>
          <div className="vnext-feature-row__name">{feature.label}</div>
        </td>
        {isDevStaff ? (
          <td>
            <VNextSwitch
              checked={ready}
              disabled={readinessState.isLoading}
              label={`Mark ${feature.label} ready for admins`}
              onChange={(next) => void toggleReadiness(next)}
            />
            {!ready ? (
              <div className="vnext-feature-row__hint">In development</div>
            ) : null}
          </td>
        ) : null}
        {FEATURE_ACCESS_UI_ENVIRONMENTS.map(({ key }) => (
          <FeatureEnvironmentToggleCell
            canManage={canManage}
            environment={key}
            feature={feature}
            key={key}
            onBlockedNotReady={onBlockedNotReady}
            onFeedback={onFeedback}
            onRequestConfirm={onRequestConfirm}
            ready={ready}
          />
        ))}
        <td className="vnext-feature-row__disclosure-cell">
          <button
            aria-expanded={expanded}
            aria-label={
              expanded ? `Collapse ${feature.label}` : `Expand ${feature.label}`
            }
            className="vnext-feature-row__disclosure"
            onClick={() => setExpanded((value) => !value)}
            type="button"
          >
            <ExpandMoreIcon
              aria-hidden="true"
              className="vnext-feature-row__chevron"
            />
          </button>
        </td>
      </tr>
      {expanded ? (
        <tr>
          <td colSpan={columnCount}>
            <div className="vnext-feature-row__detail vnext-stack">
              <div
                aria-label={`Environment for ${feature.label}`}
                className="vnext-feature-env-tabs"
                role="tablist"
              >
                {FEATURE_ACCESS_UI_ENVIRONMENTS.map(({ key, label }) => (
                  <button
                    aria-selected={key === activeEnvironment}
                    className={`vnext-feature-env-tab${
                      key === activeEnvironment
                        ? " vnext-feature-env-tab--active"
                        : ""
                    }`}
                    key={key}
                    onClick={() => setActiveEnvironment(key)}
                    role="tab"
                    type="button"
                  >
                    {label}
                  </button>
                ))}
              </div>
              {FEATURE_ACCESS_UI_ENVIRONMENTS.map(({ key }) => (
                <div
                  className="vnext-stack"
                  hidden={key !== activeEnvironment}
                  key={key}
                >
                  <FeatureAccessEnvironmentPanel
                    canManage={canManage}
                    environment={key}
                    feature={feature}
                    onBlockedNotReady={onBlockedNotReady}
                    onFeedback={onFeedback}
                    ready={ready}
                  />
                </div>
              ))}
            </div>
          </td>
        </tr>
      ) : null}
    </tbody>
  );
};

export default FeatureAccessRow;
